import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'

const ChipListEditor = ({ label, helper, values, onChange, locked = [] }) => {
  const [newValue, setNewValue] = useState('')

  const addValue = () => {
    const value = newValue.trim()
    if (!value) return
    if (![...locked, ...values].includes(value)) {
      onChange([...values, value])
    }
    setNewValue('')
  }

  return (
    <Stack gap={1}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper}
        </Typography>
      </Box>
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {locked.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
        {values.map((value) => (
          <Chip
            key={value}
            label={value}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => onChange(values.filter((v) => v !== value))}
          />
        ))}
      </Box>
      <Box display="flex" gap={1}>
        <TextField
          size="small"
          fullWidth
          placeholder={`Add ${label.toLowerCase().replace(/s$/, '')}...`}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addValue()
            }
          }}
        />
        <Button variant="outlined" onClick={addValue}>
          Add
        </Button>
      </Box>
    </Stack>
  )
}

const TeamStep = ({
  companySettings,
  setCompanySettings,
  clinicalRole,
  setClinicalRole,
}) => (
  <Stack gap={3}>
    <Box>
      <Typography variant="h6">Teams & Clinical Roles</Typography>
      <Typography variant="body2" color="text.secondary">
        Teams control which areas of AmbuNet your staff can access. Roles
        describe clinical grades used for shifts, skill mixes and training
        requirements. The defaults suit most providers — adjust them to match
        how your organisation works.
      </Typography>
    </Box>

    <ChipListEditor
      label="Teams"
      helper="Core teams are built in. Add any extra teams specific to your organisation."
      locked={companySettings.teams ?? []}
      values={companySettings.additionalTeams ?? []}
      onChange={(additionalTeams) =>
        setCompanySettings((prev) => ({ ...prev, additionalTeams }))
      }
    />

    <ChipListEditor
      label="Clinical Roles"
      helper="Remove any grades you don't use and add ones you do."
      values={companySettings.roles ?? []}
      onChange={(roles) => setCompanySettings((prev) => ({ ...prev, roles }))}
    />

    <Stack gap={1}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Your Clinical Role
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The grade you work at yourself. It's shown next to your name across
          AmbuNet and decides which mandatory training applies to you. If none
          of the roles above fit, add one first.
        </Typography>
      </Box>
      <TextField
        select
        required
        size="small"
        fullWidth
        label="Clinical Role"
        sx={{ mt: 0.5 }}
        value={clinicalRole}
        onChange={(e) => setClinicalRole(e.target.value)}
      >
        {(companySettings.roles ?? []).map((role) => (
          <MenuItem key={role} value={role}>
            {role}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  </Stack>
)

export default TeamStep
